import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { productImageStyles, ProductImageStyles } from "@store/Overrides/Components/ProductImage";
import { ProductPriceStyles } from "@store/Overrides/Components/ProductPrice";
import { contentSpacingStyles, gridGapStyles } from "@store/theme/common/mixins";
import { TYPOGRAPHY } from "@store/theme/design";
import { css } from "styled-components";

export interface StoreProductLineProps {
    container?: GridContainerProps;
    containerWithTopBorder?: GridContainerProps;
    imageGridItem?: GridItemProps;
    infoGridItem?: GridItemProps;
    infoContainer?: GridContainerProps;
    descriptionGridItem?: GridItemProps;
    descriptionContainer?: GridContainerProps;
    brand?: ProductBrandStyles;
    headingGridItem?: GridItemProps;
    partNumbersGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    priceAndQuantityGridItem?: GridItemProps;
    priceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    price?: ProductPriceStyles;
    quantityGridItem?: GridItemProps;
    subTotalGridItem?: GridItemProps;
    subTotalPrice?: SmallHeadingAndTextStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    configurationText?: TypographyProps;
    promotionText?: TypographyProps;
    productImage?: ProductImageStyles;
    actionLink?: LinkPresentationProps;
    configurationGridItem?: GridItemProps;
    warehouseGridItem?: GridItemProps;
    warehouse?: SmallHeadingAndTextStyles;
    highDemandModeText?: TypographyPresentationProps;
}

const _descriptionTypographyProps: TypographyPresentationProps = {
    forwardAs: "h3",
    weight: TYPOGRAPHY.weight.black,
    size: TYPOGRAPHY.size.base,
    lineHeight: TYPOGRAPHY.lineHeight.small,
};

const container: GridContainerProps = {
    gap: 0,
    css: css`
        ${gridGapStyles(["20px"], [TYPOGRAPHY.spacing.base])}
        padding: 1.5em 0;
        word-break: break-word;

        @media print {
            position: relative !important;
            display: block !important;
            & > div {
                position: relative !important;
                display: block !important;
                page-break-inside: avoid;
                padding-bottom: 30px;
                margin-bottom: 0px;
                margin-top: 0px;
            }
        }
    `,
};

export const productLineProps: StoreProductLineProps = {
    container,
    containerWithTopBorder: {
        ...container,
        css: css`
            ${container.css}
            border-top: 1px solid ${getColor("common.border")};

            &:first-child {
                padding-top: 0;
                border-top: 0;
            }

            &:last-child {
                padding-bottom: 0;
            }
        `,
    },
    imageGridItem: {
        width: [4, 3, 3, 2, 2],
        printWidth: 2,
        css: css`
            flex-direction: column;
            align-items: center;
        `,
    },
    infoGridItem: {
        width: [8, 9, 9, 10, 10],
        printWidth: 10,
        css: css`
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    infoContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(["16px"], [".5em"])}

            ${GridOffset} {
                @media print {
                    display: flex !important;
                }
            }
        `,
    },
    descriptionGridItem: {
        width: [12, 12, 12, 12, 6],
        printWidth: 6,
    },
    descriptionContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles([], [".4em"])}
        `,
    },
    brand: {
        nameText: {
            color: "text.main",
            size: TYPOGRAPHY.size.smallest,
            weight: TYPOGRAPHY.weight.base,
            css: css`
                text-transform: uppercase;
            `,
        },
    },
    headingGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    partNumbersGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
    },
    priceAndQuantityGridItem: {
        width: [12, 12, 12, 12, 6],
        printWidth: 6,
        css: css`
            align-items: center;
        `,
    },
    priceAndQuantityContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(["16px"], [".5em"])}
        `,
    },
    priceGridItem: {
        width: [12, 5, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    price: {
        price: {
            priceText: {
                weight: TYPOGRAPHY.weight.base,
            },
            errorText: {
                weight: TYPOGRAPHY.weight.base,
            },
        },
        retailPrice: {
            weight: TYPOGRAPHY.weight.base,
        },
    },
    quantityGridItem: {
        width: [4, 3, 2, 3, 3],
    },
    subTotalGridItem: {
        width: [8, 4, 5, 4, 4],
    },
    warehouseGridItem: {
        width: 12,
    },
    subTotalPrice: {
        text: {
            weight: TYPOGRAPHY.weight.bold,
        },
    },
    productDescription: {
        productDetailLink: {
            typographyProps: _descriptionTypographyProps,
            css: css`
                &:hover {
                    text-decoration: underline;
                }
            `,
        },
        productDetailDescription: _descriptionTypographyProps,
    },
    productPartNumbers: {
        container: {
            gap: 0,
            css: css`
                ${gridGapStyles([], ["0.2em"])}
                font-size: ${TYPOGRAPHY.size.smaller};
                line-height: ${TYPOGRAPHY.lineHeight.small};
            `,
        },
    },
    configurationText: {
        size: TYPOGRAPHY.size.smaller,
        color: "text.accent",
        forwardAs: "p",
    },
    promotionText: {
        italic: true,
        size: TYPOGRAPHY.size.tiny,
        color: "text.accent",
        forwardAs: "p",
        lineHeight: TYPOGRAPHY.lineHeight.small,
        css: css`
            ${contentSpacingStyles(null, "0.2em")}
        `,
    },
    productImage: {
        wrapper: {
            css: css`
                ${productImageStyles.wrapper?.css}
                ${contentSpacingStyles(null, "0.5em")}
            `,
        },
    },
    actionLink: {
        css: css`
            ${props => props.theme?.link?.defaultProps?.css}
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.small};
            text-align: center;
            ${contentSpacingStyles(null, "0.5em")}
        `,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    warehouse: {
        text: {
            weight: TYPOGRAPHY.weight.bold,
        },
    },
    highDemandModeText: {
        color: "text.accent",
        italic: true,
        size: TYPOGRAPHY.size.smallest,
        weight: TYPOGRAPHY.weight.bold,
    },
};
